/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 *
 * While open, the window is a dialog (role="dialog", aria-modal) named by its title: `titleId`
 * if given, else the `modalTitle` heading, else the first h1/h2 inside it, else `ariaLabel`.
 * Focus moves into it when it opens, Tab stays inside it, and focus goes back to where it was
 * when it closes. Escape closes the top-most open window only.
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { Button, Heading, IconClose } from '../../components';

import css from './Modal.module.css';

const KEY_CODE_ESCAPE = 27;

const TABBABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',');

// Open windows in the order they were opened. Only the last one (on top) traps Tab and closes on
// Escape: with a window opened from another window, Escape used to close both at once.
const openModals = [];
// One Escape keyup reaches every window's listener; once a window has closed on it, the one below
// is on top by the time its listener runs and must not close too.
let lastHandledEscape = null;

const isTopModal = modal => openModals.length === 0 || openModals[openModals.length - 1] === modal;

// Hidden steps of a wizard stay in the DOM, and Tab skips them, so the ends of the Tab cycle must
// be the first and last elements the user can actually reach.
const isVisible = (el, container) => {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
  }
  if (window.getComputedStyle(el).visibility === 'hidden') {
    return false;
  }
  for (let node = el; node && node !== container; node = node.parentElement) {
    if (node.hidden || window.getComputedStyle(node).display === 'none') {
      return false;
    }
  }
  return true;
};

export const tabbablesIn = container => {
  const candidates = Array.from(container.querySelectorAll(TABBABLE_SELECTOR)).filter(
    el => el.tabIndex >= 0 && isVisible(el, container)
  );
  // Tab reaches a radio group once, through its checked radio (or its first one if none is).
  return candidates.filter(el => {
    if (el.type !== 'radio' || !el.name) {
      return true;
    }
    const group = candidates.filter(r => r.type === 'radio' && r.name === el.name);
    const checked = group.find(r => r.checked);
    return el === (checked || group[0]);
  });
};

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

export class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.refDiv = React.createRef();
    this.vh = null;
    this.isActive = false;
    // A window rendered already open (e.g. only mounted when needed) gets focus from whatever had
    // it before its contents mounted, so that's taken now, before they can move it.
    this.opener = props.isOpen && typeof document !== 'undefined' ? document.activeElement : null;

    this.state = {
      portalRoot: null,
      autoTitleId: null,
    };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, isOpen);
    window.document.body.addEventListener('keyup', this.handleBodyKeyUp);

    // A hack to update container height for mobile Safari,
    // when resizing happens due to scroll.
    // css.isOpenInPortal has "height: calc(var(--vh, 1vh) * 100)"
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
    window.addEventListener('resize', this.handleResize);

    this.setState({
      portalRoot: document.getElementById('portal-root'),
    });

    // Without a portal the window is in the DOM already; with one, it is after the re-render.
    this.syncDialog();
  }

  getSnapshotBeforeUpdate(prevProps) {
    // Taken before the opening render: by componentDidUpdate the contents may have moved focus.
    if (!prevProps.isOpen && this.props.isOpen) {
      this.opener = document.activeElement;
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    const { id, isOpen, onManageDisableScrolling } = prevProps;
    if (this.props.isOpen !== isOpen) {
      onManageDisableScrolling(id, this.props.isOpen);
    }
    this.syncDialog();
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling } = this.props;
    window.document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    window.removeEventListener('resize', this.handleResize);
    this.deactivate();
    onManageDisableScrolling(id, false);
  }

  // Starts or ends the dialog behaviour when the window opens or closes, and keeps its name
  // pointing at the title it shows now.
  syncDialog() {
    const container = this.refDiv.current;
    if (this.props.isOpen && container && !this.isActive) {
      this.activate(container);
    } else if (!this.props.isOpen && this.isActive) {
      this.deactivate();
    }
    if (this.isActive) {
      this.findAutoTitle(container);
    }
  }

  activate(container) {
    this.isActive = true;
    openModals.push(this);
    document.addEventListener('keydown', this.handleDocumentKeyDown);
    // A field inside that took focus itself (autoFocus) keeps it.
    if (!container.contains(document.activeElement)) {
      container.focus();
    }
  }

  deactivate() {
    if (!this.isActive) {
      return;
    }
    this.isActive = false;
    const index = openModals.indexOf(this);
    if (index !== -1) {
      openModals.splice(index, 1);
    }
    document.removeEventListener('keydown', this.handleDocumentKeyDown);

    const opener = this.opener;
    this.opener = null;
    const container = this.refDiv.current;
    const active = document.activeElement;
    // Focus left inside the closed window (or dropped to the page) goes back to where it was;
    // focus that something else already moved on (e.g. into the next window) stays there.
    const focusIsLost =
      !active || active === document.body || (container && container.contains(active));
    if (opener && opener !== document.body && document.body.contains(opener) && focusIsLost) {
      opener.focus({ preventScroll: true });
    }
  }

  // A window without a given title is named by its first h1/h2, the title the user sees.
  findAutoTitle(container) {
    const { titleId, modalTitle } = this.props;
    let autoTitleId = null;
    if (!titleId && !modalTitle && container) {
      const heading = container.querySelector('h1, h2');
      if (heading) {
        if (!heading.id) {
          heading.id = `${this.props.id}.title`;
        }
        autoTitleId = heading.id;
      }
    }
    if (autoTitleId !== this.state.autoTitleId) {
      this.setState({ autoTitleId });
    }
  }

  handleBodyKeyUp(event) {
    const { isOpen } = this.props;
    if (
      event.keyCode === KEY_CODE_ESCAPE &&
      isOpen &&
      event !== lastHandledEscape &&
      isTopModal(this)
    ) {
      lastHandledEscape = event;
      this.handleClose(event);
    }
  }

  // Keeps Tab and Shift+Tab cycling inside the top window.
  handleDocumentKeyDown(event) {
    const container = this.refDiv.current;
    if (event.key !== 'Tab' || !container || !isTopModal(this)) {
      return;
    }
    const active = document.activeElement;
    const isInside = container.contains(active);
    // Focus on something outside the window that isn't the page itself is a third-party layer
    // (e.g. a card check opened by the payment provider): leave its keyboard alone.
    if (!isInside && active && active !== document.body) {
      return;
    }

    const tabbables = tabbablesIn(container);
    if (tabbables.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    if (event.shiftKey && (!isInside || active === first || active === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!isInside || active === last || active === container)) {
      event.preventDefault();
      first.focus();
    }
  }

  // The scroll lock is released when the window really closes (componentDidUpdate, unmount):
  // a window that refuses to close while busy keeps the page locked behind it.
  handleClose(event) {
    this.props.onClose(event);
  }

  handleResize() {
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
  }

  render() {
    const {
      children,
      className,
      scrollLayerClassName,
      containerClassName,
      contentClassName,
      lightCloseButton,
      intl,
      isClosedClassName,
      isOpen,
      isSearchFilterModal,
      usePortal,
      isCarrierModal,
      modalTitle,
      id,
      titleId,
      ariaLabel,
    } = this.props;

    const closeModalMessage = intl.formatMessage({ id: 'Modal.closeModal' });
    const closeButtonClasses = classNames(css.close, {
      [css.closeLight]: lightCloseButton,
    });
    const closeBtn = isOpen ? (
      <Button
        onClick={this.handleClose}
        rootClassName={closeButtonClasses}
        title={closeModalMessage}
      >
        {/* <span className={css.closeText}>
          {closeButtonMessage || <FormattedMessage id="Modal.close" />}
        </span> */}
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : null;

    const modalTitleId = `${id}.modalTitle`;
    const header = modalTitle ? (
      <div className={css.modalHead}>
        <Heading as="h2" rootClassName={css.modalTitle} id={modalTitleId}>
          {modalTitle}
        </Heading>
        {closeBtn}
      </div>
    ) : (
      <>{closeBtn}</>
    );

    // Only an open window is a dialog: closed ones stay in the DOM (hidden), and ModalInMobile's
    // content is part of the page on desktop.
    const labelledBy = titleId || (modalTitle ? modalTitleId : this.state.autoTitleId);
    const dialogProps = isOpen
      ? {
          role: 'dialog',
          'aria-modal': 'true',
          'aria-labelledby': labelledBy || undefined,
          'aria-label': labelledBy ? undefined : ariaLabel || undefined,
        }
      : {};

    // Modal uses given styles to wrap child components.
    // If props doesn't contain isClosedClassName, styles default to css.isClosed
    // This makes it possible to create ModalInMobile on top of Modal where style modes are:
    // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
    const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
    const modalClass = isOpen ? isOpenClass : isClosedClassName;
    const classes = classNames(
      modalClass,
      className,
      isSearchFilterModal ? css.searchFilterModal : null,
      isCarrierModal ? css.carrierModal : null
    );
    const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
    const containerClasses = containerClassName || css.container;
    const portalRoot = this.state.portalRoot;

    // If you want to use Portal https://reactjs.org/docs/portals.html
    // you need to use 'userPortal' flag.
    // ModalInMobile component needs to use the old Modal without the portal
    // because it's relying that the content is rendered inside
    // the DOM hierarchy of the parent component unlike Modal inside Portal.

    // In place, the container takes focus only while open: on desktop ModalInMobile's content is
    // part of the page and a click in it must not focus the wrapper.
    return !usePortal ? (
      <div className={classes}>
        <div className={scrollLayerClasses}>
          <div
            className={classNames(containerClasses, { [css.focusedDiv]: isOpen })}
            ref={this.refDiv}
            tabIndex={isOpen ? '-1' : undefined}
            {...dialogProps}
          >
            {header}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    ) : portalRoot ? (
      <Portal portalRoot={portalRoot}>
        <div className={classes}>
          <div className={scrollLayerClasses}>
            <div
              className={classNames(containerClasses, css.focusedDiv)}
              ref={this.refDiv}
              tabIndex="-1"
              {...dialogProps}
            >
              {header}
              <div className={classNames(contentClassName || css.content)}>{children}</div>
            </div>
          </div>
        </div>
      </Portal>
    ) : null;
  }
}

ModalComponent.defaultProps = {
  children: null,
  className: null,
  scrollLayerClassName: null,
  containerClassName: null,
  contentClassName: null,
  lightCloseButton: false,
  isClosedClassName: css.isClosed,
  isOpen: false,
  onClose: null,
  usePortal: false,
  titleId: null,
  ariaLabel: null,
};

const { bool, func, node, string } = PropTypes;

ModalComponent.propTypes = {
  children: node,
  className: string,
  scrollLayerClassName: string,
  containerClassName: string,
  contentClassName: string,
  lightCloseButton: bool,
  id: string.isRequired,
  intl: intlShape.isRequired,
  isClosedClassName: string,
  isOpen: bool,
  onClose: func.isRequired,
  usePortal: bool,
  // id of the window's own title element, which names the dialog
  titleId: string,
  // the dialog's name when it has no visible title (already translated)
  ariaLabel: string,

  // eslint-disable-next-line react/no-unused-prop-types
  onManageDisableScrolling: func.isRequired,
};

export default injectIntl(ModalComponent);
